import { useQuery } from '@tanstack/react-query'
import { Lock, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Input } from '@/components/ui/input'
import { usePermission } from '@/hooks/usePermission'
import { getRoles } from '@/services/api/permission.service'
import { getWorkspaceUsers } from '@/services/api/task.service'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import type { FieldAccess } from '@/types/api/form'
import { cn } from '@/utils/cn'

interface FieldAccessEditorProps {
  value: FieldAccess | null | undefined
  onChange: (next: FieldAccess) => void
  /** Why the field cannot be hidden from anyone (the title field names the record in lists and links). */
  mustStayVisible?: string
}

type ListKey = 'view' | 'edit'

const MODES: Array<[FieldAccess['mode'], string]> = [
  ['OPEN', 'Everyone who can open the record'],
  ['EDIT_RESTRICTED', 'Everyone can see it; only specific roles and people can edit'],
  ['RESTRICTED', 'Only specific roles and people can see it'],
]

const LISTS: Array<{ key: ListKey; label: string; hint: string }> = [
  { key: 'view', label: 'Can view', hint: 'See the value, filter and sort by it.' },
  { key: 'edit', label: 'Can edit', hint: 'Change the value. Editing includes viewing.' },
]

const toggled = (list: string[], id: string) => (list.includes(id) ? list.filter((v) => v !== id) : [...list, id])

export const OPEN_ACCESS: FieldAccess = { mode: 'OPEN' }

/** True if the field limits who sees it or who edits it. */
export function isRestricted(access: FieldAccess | null | undefined): boolean {
  return !!access && access.mode !== 'OPEN'
}

/**
 * Who may see and who may change one form field. Owners and admins always can and are not listed;
 * everyone else needs a role or a person in the lists below. Shown to owners and admins only.
 */
export function FieldAccessEditor({ value, onChange, mustStayVisible }: FieldAccessEditorProps) {
  const { canAnywhere, isOwner, isAdmin } = usePermission()
  const canListRoles = isOwner || isAdmin || canAnywhere(PERMISSION_KEYS.ROLE_VIEW)
  const { data: roles = [] } = useQuery({ queryKey: ['roles'], queryFn: getRoles, enabled: canListRoles })
  const { data: users = [] } = useQuery({ queryKey: ['workspace-users', 'light'], queryFn: getWorkspaceUsers })
  const usersById = useMemo(() => new Map(users.map((u) => [u.userId, u])), [users])

  const access = value ?? OPEN_ACCESS
  const restricted = isRestricted(access)
  const editOnly = access.mode === 'EDIT_RESTRICTED'
  const set = (patch: Partial<FieldAccess>) => onChange({ ...access, ...patch })

  return (
    <fieldset className="space-y-3">
      <legend className="sr-only">Who can see and edit this field</legend>
      <div className="flex flex-col gap-1.5">
        {MODES.map(([mode, text]) => {
          const unavailable = mode === 'RESTRICTED' && !!mustStayVisible && access.mode !== mode
          return (
            <label key={mode} className={cn('flex items-center gap-2 text-sm', unavailable ? 'cursor-not-allowed text-neutral-400' : 'cursor-pointer')}>
              <input
                type="radio"
                checked={access.mode === mode}
                disabled={unavailable}
                onChange={() => onChange(mode === 'OPEN' ? OPEN_ACCESS : { ...access, mode })}
              />
              {text}
            </label>
          )
        })}
      </div>
      {mustStayVisible && <p className="text-xs text-neutral-500">{mustStayVisible}</p>}

      {restricted && (
        <div className="space-y-3">
          <p className="text-xs text-neutral-500">
            Owners and admins always have full access.{' '}
            {editOnly ? 'Everyone else can see this field but not change it.' : 'Anyone not listed cannot see this field at all.'}
          </p>
          {LISTS.filter((list) => !editOnly || list.key === 'edit').map((list) => {
            const roleIds = (access[`${list.key}RoleIds`] ?? []) as string[]
            const userIds = (access[`${list.key}UserIds`] ?? []) as string[]
            return (
              <AccessList
                key={list.key}
                label={list.label}
                hint={list.hint}
                roles={roles.filter((r) => r.active)}
                canListRoles={canListRoles}
                users={users}
                usersById={usersById}
                roleIds={roleIds}
                userIds={userIds}
                onRoles={(ids) => set({ [`${list.key}RoleIds`]: ids })}
                onUsers={(ids) => set({ [`${list.key}UserIds`]: ids })}
              />
            )
          })}
          {isNobody(access) && (
            <p className="flex items-center gap-1.5 text-xs text-amber-700">
              <Lock className="h-3 w-3" /> Only owners and admins can {editOnly ? 'edit' : 'see'} this field right now.
            </p>
          )}
        </div>
      )}
    </fieldset>
  )
}

function isNobody(access: FieldAccess): boolean {
  const lists = access.mode === 'EDIT_RESTRICTED'
    ? [access.editRoleIds, access.editUserIds]
    : [access.viewRoleIds, access.viewUserIds, access.editRoleIds, access.editUserIds]
  return lists.every((ids) => (ids ?? []).length === 0)
}

interface AccessListProps {
  label: string
  hint: string
  roles: Array<{ id: string; name: string }>
  canListRoles: boolean
  users: Array<{ userId: string; name: string; email: string }>
  usersById: Map<string, { userId: string; name: string; email: string }>
  roleIds: string[]
  userIds: string[]
  onRoles: (ids: string[]) => void
  onUsers: (ids: string[]) => void
}

function AccessList({
  label, hint, roles, canListRoles, users, usersById, roleIds, userIds, onRoles, onUsers,
}: AccessListProps) {
  const [search, setSearch] = useState('')
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return []
    return users
      .filter((u) => !userIds.includes(u.userId))
      .filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .slice(0, 6)
  }, [users, userIds, search])

  return (
    <div className="space-y-1.5 rounded-lg border border-neutral-200 bg-neutral-50/60 p-2.5">
      <p className="text-xs font-semibold text-neutral-700">
        {label} <span className="font-normal text-neutral-500">· {hint}</span>
      </p>
      {canListRoles && (
        <div className="flex flex-wrap gap-1.5">
          {roles.length === 0 && <span className="text-xs italic text-neutral-400">No roles</span>}
          {roles.map((role) => (
            <button
              key={role.id}
              type="button"
              aria-pressed={roleIds.includes(role.id)}
              onClick={() => onRoles(toggled(roleIds, role.id))}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs transition-colors',
                roleIds.includes(role.id)
                  ? 'border-primary-500 bg-primary-50 font-semibold text-primary-800'
                  : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-100'
              )}
            >
              {role.name}
            </button>
          ))}
        </div>
      )}
      {userIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {userIds.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-full border border-primary-500 bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-800"
            >
              {usersById.get(id)?.name ?? 'Unknown member'}
              <button
                type="button"
                aria-label={`Remove ${usersById.get(id)?.name ?? 'member'}`}
                onClick={() => onUsers(userIds.filter((u) => u !== id))}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Input
        aria-label={`Add a person who ${label.toLowerCase()}`}
        placeholder="Add a person by name or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="h-8 text-sm"
      />
      {search.trim() && (
        <ul className="max-h-36 divide-y overflow-y-auto rounded-md border bg-white text-sm">
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-xs italic text-neutral-400">No matching people</li>
          ) : (
            matches.map((user) => (
              <li key={user.userId}>
                <button
                  type="button"
                  className="flex w-full flex-col items-start px-3 py-1.5 text-left hover:bg-neutral-50"
                  onClick={() => {
                    onUsers([...userIds, user.userId])
                    setSearch('')
                  }}
                >
                  <span className="font-medium">{user.name}</span>
                  <span className="text-xs text-neutral-500">{user.email}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}
